import { SocialLoginButton, SocialProvider } from '@inflearn/app-shell';
import { Button, Flex, PasswordInput, Text, TextInput, Title } from '@inflearn/ds-react';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { ApiError } from '../../../../resources/ApiError';
import { logger } from '../../../../resources/Logger';
import { AUTH_URL } from '../../../constants/urls';
import { useLastAuthProvider } from '../../../hooks/useLastAuthProvider';
import { useUserCollectInfoCreateMutation } from '../../../mutations/useAuthUserCollectInfoCreateMutation';
import useClientAuthSigninCreateMutation from '../../../mutations/useClientAuthSigninCreateMutation';
import useClientAuthSsoRedirectQuery from '../../../queries/useClientAuthSsoRedirectQuery';
import { eventNames } from '../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../utils/GTM/useTrackMixpanelEvent';
import { REFERER_TYPE, getClientUserRefererObject } from '../../../utils/refererObject';
import { ERROR_MESSAGE } from '../../../validators/constants';
import AuthAnchors from '../../common/AuthAnchors';
import { SignupDescription } from '../signup/SignupDescription';
import SocialLoginDivider from '../signup/SocialLoginDivider';
import type { EmailFormValue } from './find/password/EmailFormItem';
import type { PasswordFormValue } from './update/password/PasswordVerifyFormItems';

type SigninFormValue = EmailFormValue & Omit<PasswordFormValue, 'passwordConfirm'>;

export const SigninPageItem = () => {
  const { t } = useTranslation('common');
  const { register, handleSubmit } = useForm<SigninFormValue>();

  const { mutate: sendUserInfo } = useUserCollectInfoCreateMutation();

  const { mutate, isLoading, isSuccess } = useClientAuthSigninCreateMutation({
    onError: (error) => {
      if (ApiError.isForbidden(error)) {
        location.href = AUTH_URL.VERIFY_EMAIL() + location.search;

        return;
      }
      alert(ERROR_MESSAGE.LOGIN_FAILED());

      if (ApiError.isApiError(error)) {
        return;
      }

      logger.error(error);
    },
    onSuccess: () => {
      sendUserInfo({
        ...getClientUserRefererObject(),
        type: REFERER_TYPE.LOGGED_IN,
      });
    },
  });

  useClientAuthSsoRedirectQuery({ enabled: isSuccess });
  const { trackMixpanelEvent } = useTrackMixpanelEvent();
  const { lastAuthProvider } = useLastAuthProvider();

  useEffect(() => {
    trackMixpanelEvent(eventNames.VIEW_SIGNIN_PAGE);
  }, [trackMixpanelEvent]);

  const handleFormSubmit: SubmitHandler<SigninFormValue> = (formValue) => {
    mutate({ ...formValue, email: formValue.email.trim() });
  };

  const handleFormInvalid = () => {
    alert(ERROR_MESSAGE.LOGIN_FAILED());
  };

  return (
    <>
      <Flex direction="column" justify="center" align="center" mb={24} gap={4}>
        <Title order={2}>{t('로그인')}</Title>
        <Text size="md">{t('인프런 계정으로 로그인할 수 있어요')}</Text>
      </Flex>
      <Flex
        component="form"
        direction="column"
        gap={16}
        w="100%"
        mb={10}
        onSubmit={handleSubmit(handleFormSubmit, handleFormInvalid)}
      >
        <TextInput
          size="md"
          label={t('이메일')}
          placeholder="example@inflab.com"
          {...register('email', { required: true })}
        />

        <PasswordInput
          size="md"
          label={t('비밀번호')}
          placeholder="********"
          {...register('password', { required: true })}
        />

        <Button
          size="md"
          type="submit"
          variant="filled"
          fullWidth
          mt={16}
          loading={isLoading}
          onClick={() =>
            trackMixpanelEvent(eventNames.CLICK_SIGNIN_AT_SIGNIN, {
              spot: 'page',
            })
          }
        >
          {t('로그인')}
        </Button>
      </Flex>
      <AuthAnchors />
      <Flex direction="column" align="center" gap="xl" w="100%">
        <SocialLoginDivider>{t('간편 로그인')}</SocialLoginDivider>
        <Flex justify="center" gap={8}>
          {[
            SocialProvider.Kakao,
            SocialProvider.Google,
            SocialProvider.Github,
            SocialProvider.Apple,
          ].map((socialProvider) => (
            <SocialLoginButton
              key={socialProvider}
              provider={socialProvider}
              showTooltip={lastAuthProvider === socialProvider}
              isAuthPage
            />
          ))}
        </Flex>
        <SignupDescription />
      </Flex>
    </>
  );
};
